import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Alert,
  AlertDescription,
  Button,
  FileDropZone,
  GeoSelect,
  TagInput,
  Input,
  ProgressBar,
  SegmentedControl,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  cn,
} from "@fox1ck-org/ui";
import { Form } from "@fox1ck-org/ui/form";
import { toast } from "@fox1ck-org/ui/toast";
import { Check, ExternalLink, FileArchive, Link2, Sparkles, X } from "lucide-react";
import { Lander, LanderType, RiskStatus } from "@gen/tracker/v1/lander_pb";

import { countrySuggestions } from "@/lib/countries";
import { RhfField } from "@/components/rhf-field";
import { getLocale, useT, type TKey } from "@/lib/i18n";
import { landerCreateSchema, type LanderCreateValues } from "@/lib/schemas/lander";
import { useCreateLander, useImportLanderPage, useUploadLanderAsset } from "@/lib/services/lander-service";

import { LANDER_TYPES, RISKS, riskLabelKey, typeLabelKey } from "./kinds";

export type CreateSource = "zip" | "link" | "studio";

/** Where the landing studio lives; a studio page arrives here by its own «Опубликовать». */
export const STUDIO_URL = "https://landing-studio.igrudsky.dev";
const MAX_ZIP = 50 * 1024 * 1024;

const SOURCES: { id: CreateSource; title: TKey; body: TKey; icon: typeof FileArchive }[] = [
  { id: "zip", title: "landers.create.zip", body: "landers.create.zip.body", icon: FileArchive },
  { id: "link", title: "landers.create.link", body: "landers.create.link.body", icon: Link2 },
  { id: "studio", title: "landers.create.studio", body: "landers.create.studio.body", icon: Sparkles },
];

const mb = (n: number) => (n / 1024 / 1024).toLocaleString("ru-RU", { maximumFractionDigits: 1 });

/** «andrzej-kowalski-v1.zip» → «andrzej-kowalski-v1»; a link → its last path word or host. Pure. */
export function nameFrom(source: string): string {
  const s = source.trim();
  if (!s) return "";
  if (/\.zip$/i.test(s)) return s.replace(/\.zip$/i, "");
  try {
    const u = new URL(s);
    const last = u.pathname.split("/").filter(Boolean).pop();
    return (last ? last.replace(/\.[a-z]+$/i, "") : u.hostname).slice(0, 200);
  } catch {
    return "";
  }
}

/**
 * The name a new source proposes takes the field only while the field holds
 * nothing of the person's own: empty, or still the name the last source
 * proposed. Anything typed stays. null = leave the field alone. Pure.
 */
export function proposedName(current: string, auto: string | null, proposed: string): string | null {
  if (!proposed) return null;
  return !current || current === auto ? proposed : null;
}

const EMPTY: LanderCreateValues = { source: "zip", name: "", type: LanderType.LANDING, language: "", countries: [], risk: RiskStatus.SAFE, url: "" };

/**
 * Новый ленд (board LanderCreate): from a ZIP, from a page's address, or from
 * the landing studio. A ZIP and a link make the lander and its first version
 * in one go — CreateLander, then the upload (`POST …/assets`) or the capture
 * (`POST …/assets/import`) — and open it on «Просмотр», where the checks are.
 * A studio page is published from the studio itself.
 */
export function LanderCreate({ source, onSourceChange }: { source: CreateSource | null; onSourceChange: (s: CreateSource | null) => void }) {
  const t = useT();
  const navigate = useNavigate();
  const create = useCreateLander();
  const upload = useUploadLanderAsset();
  const capture = useImportLanderPage();
  const [file, setFile] = useState<File | null>(null);
  const [progress, setProgress] = useState<{ loaded: number; total: number } | null>(null);
  // The name the archive or the address proposed — a suggestion: the field
  // selects it whole on focus, so typing replaces it instead of appending.
  const [autoName, setAutoName] = useState<string | null>(null);
  const selectedOnFocus = useRef(false);
  const schema = useMemo(() => landerCreateSchema(t), [t]);
  const form = useForm<LanderCreateValues>({ resolver: zodResolver(schema), defaultValues: EMPTY });
  const open = source !== null;
  const current = form.watch("source");
  const busy = create.isPending || upload.isPending || capture.isPending;

  const close = () => {
    if (busy) return;
    onSourceChange(null);
    form.reset(EMPTY);
    setFile(null);
    setProgress(null);
    setAutoName(null);
  };
  const propose = (from: string) => {
    const next = proposedName(form.getValues("name"), autoName, nameFrom(from));
    if (next === null) return;
    form.setValue("name", next, { shouldValidate: true });
    setAutoName(next);
  };
  const pick = (s: CreateSource) => onSourceChange(s);
  // The sheet opens on the source it was asked for (the empty state's «Взять из студии»).
  useEffect(() => {
    if (source) form.setValue("source", source);
  }, [source, form]);

  const takeFile = (files: File[]) => {
    const f = files[0];
    if (!f) return;
    if (!/\.zip$/i.test(f.name)) return void toast.error(t("landers.create.zipOnly"));
    if (f.size > MAX_ZIP) return void toast.error(t("landers.create.tooBig"));
    setFile(f);
    propose(f.name);
  };

  const submit = form.handleSubmit(async (v) => {
    if (v.source === "zip" && !file) return void toast.error(t("landers.create.needZip"));
    let id = "";
    try {
      const saved = await create.mutateAsync(
        new Lander({ name: v.name.trim(), landerType: v.type, language: v.language, countryTargets: v.countries, riskStatus: v.risk, numberOfCtas: 1 }),
      );
      id = saved.id;
      if (v.source === "zip" && file) {
        setProgress({ loaded: 0, total: file.size });
        await upload.mutateAsync({ landerId: id, file, note: t("landers.create.firstNote"), onProgress: (loaded, total) => setProgress({ loaded, total }) });
      } else if (v.source === "link") {
        const res = await capture.mutateAsync({ landerId: id, url: v.url.trim(), note: t("landers.create.fromLink") });
        if (res.report.skipped.length > 0) toast.warning(t("landers.create.skipped", { n: res.report.skipped.length }));
      }
      toast.success(t("landers.create.done", { name: saved.name }));
      onSourceChange(null);
      form.reset(EMPTY);
      setFile(null);
      setProgress(null);
      setAutoName(null);
      navigate(`/library/landers/${id}`);
    } catch (err) {
      const msg = err instanceof Error ? err.message : t("landers.create.failed");
      if (id) {
        // The lander exists; only its page did not arrive. Its versions tab takes the next try.
        toast.error(t("landers.create.pageFailed", { error: msg }));
        onSourceChange(null);
        navigate(`/library/landers/${id}/versions`);
      } else {
        toast.error(msg);
      }
      setProgress(null);
    }
  });

  const name = form.watch("name");
  const autoNote = autoName && name === autoName ? t(current === "link" ? "landers.create.nameFromLink" : "landers.create.nameFromZip") : undefined;
  const locale = getLocale();
  const pct = progress && progress.total > 0 ? Math.round((progress.loaded / progress.total) * 100) : 0;

  return (
    <Sheet open={open} onOpenChange={(o) => (o ? undefined : close())}>
      <SheetContent side="right" closeLabel={t("landers.create.close")} className="flex w-145 max-w-[calc(100vw_-_--spacing(12))] flex-col gap-0 p-0 sm:max-w-none">
        <SheetHeader className="shrink-0 gap-1 border-b border-border px-5 pb-3 pt-4 text-left">
          <SheetTitle className="text-section-title">{t("landers.create.title")}</SheetTitle>
          <SheetDescription className="text-xs text-muted-foreground">{t("landers.create.description")}</SheetDescription>
        </SheetHeader>
        <Form {...form}>
          <form onSubmit={submit} className="flex min-h-0 flex-1 flex-col">
            <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-5 py-4">
              <div>
                <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("landers.create.from")}</div>
                <div role="radiogroup" aria-label={t("landers.create.from")} className="grid grid-cols-3 gap-2">
                  {SOURCES.map((s) => {
                    const on = current === s.id;
                    const Icon = s.icon;
                    return (
                      <button
                        key={s.id}
                        type="button"
                        role="radio"
                        aria-checked={on}
                        onClick={() => pick(s.id)}
                        className={cn(
                          "flex flex-col gap-1.5 rounded-lg border p-3 text-left transition-colors",
                          on ? "border-primary bg-primary-soft" : "border-border bg-card hover:border-border-strong",
                        )}
                      >
                        <Icon className={cn("size-5", on ? "text-primary" : "text-muted-foreground")} strokeWidth={1.5} />
                        <b className="text-sm font-medium text-foreground">{t(s.title)}</b>
                        <span className="text-xs text-muted-foreground">{t(s.body)}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {current === "zip" ? (
                file ? (
                  <div className="flex items-center gap-3 rounded-lg border border-border bg-card px-3 py-2.5">
                    <FileArchive className="size-5 shrink-0 text-muted-foreground" strokeWidth={1.5} />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-baseline justify-between gap-2">
                        <span className="truncate font-mono text-xs text-foreground">{file.name}</span>
                        <span className="whitespace-nowrap font-mono text-xs text-muted-foreground">
                          {progress ? t("landers.create.progress", { loaded: mb(progress.loaded), total: mb(progress.total), pct }) : t("landers.create.size", { size: mb(file.size) })}
                        </span>
                      </div>
                      <ProgressBar showPercent={false} value={progress?.loaded ?? 0} max={progress?.total || file.size} size="xs" className="mt-1.5" />
                    </div>
                    <Button type="button" variant="ghost" size="icon" disabled={busy} onClick={() => setFile(null)} aria-label={t("landers.create.removeZip")}>
                      <X className="size-4" />
                    </Button>
                  </div>
                ) : (
                  <FileDropZone onFiles={takeFile} accept=".zip,application/zip" label={t("landers.create.dropHere")} className="rounded-lg">
                    <label className="flex cursor-pointer flex-col items-center gap-1 rounded-lg border border-dashed border-border-strong px-4 py-6 text-center">
                      <FileArchive className="size-6 text-muted-foreground" strokeWidth={1.5} />
                      <span className="text-sm text-foreground">{t("landers.create.drop")}</span>
                      <span className="text-xs text-muted-foreground">{t("landers.create.dropRules")}</span>
                      <input type="file" accept=".zip,application/zip" className="sr-only" onChange={(e) => takeFile([...(e.target.files ?? [])])} />
                    </label>
                  </FileDropZone>
                )
              ) : null}

              {current === "link" ? (
                <RhfField control={form.control} name="url" label={t("landers.create.url")} plain description={t("landers.create.url.hint")}>
                  {(field) => (
                    <Input
                      {...field}
                      inputMode="url"
                      placeholder="https://"
                      className="font-mono"
                      onBlur={() => {
                        field.onBlur();
                        propose(field.value);
                      }}
                    />
                  )}
                </RhfField>
              ) : null}

              {current === "studio" ? (
                <Alert>
                  <Sparkles className="size-4" />
                  <AlertDescription className="flex flex-col gap-2">
                    <span>{t("landers.create.studio.how")}</span>
                    <Button asChild variant="secondary" size="sm" className="self-start">
                      <a href={STUDIO_URL} target="_blank" rel="noreferrer">
                        <ExternalLink className="size-4" strokeWidth={1.5} />
                        {t("landers.create.studio.open")}
                      </a>
                    </Button>
                  </AlertDescription>
                </Alert>
              ) : (
                <>
                  <RhfField control={form.control} name="name" label={t("landers.f.name")} plain description={autoNote}>
                    {(field) => (
                      <Input
                        {...field}
                        font="sans"
                        placeholder={t("landers.create.namePlaceholder")}
                        onFocus={(e) => {
                          if (!autoName || field.value !== autoName) return;
                          e.currentTarget.select();
                          selectedOnFocus.current = true;
                        }}
                        onMouseUp={(e) => {
                          // A click's mouseup would put the caret down and drop the selection.
                          if (selectedOnFocus.current) e.preventDefault();
                          selectedOnFocus.current = false;
                        }}
                      />
                    )}
                  </RhfField>
                  <div className="grid grid-cols-2 gap-3">
                    <RhfField control={form.control} name="type" label={t("landers.f.type")} plain>
                      {(field) => (
                        <Select value={String(field.value)} onValueChange={(v) => field.onChange(Number(v))}>
                          <SelectTrigger aria-label={t("landers.f.type")}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {LANDER_TYPES.map((x) => (
                              <SelectItem key={x} value={String(x)}>
                                {t(typeLabelKey(x))}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    </RhfField>
                    <RhfField control={form.control} name="language" label={t("landers.f.language")} plain>
                      {(field) => <GeoSelect kind="language" value={field.value} onChange={field.onChange} locale={locale} allowEmpty emptyLabel={t("landers.f.language.none")} placeholder={t("landers.f.language.pick")} />}
                    </RhfField>
                  </div>
                  <RhfField control={form.control} name="countries" label={t("landers.f.countries")} plain>
                    {(field) => <TagInput mode="values" mono allowCustom={false} value={field.value} onChange={field.onChange} suggestions={countrySuggestions(locale)} placeholder={t("landers.f.countries.add")} labels={{ input: t("landers.f.countries"), remove: (v) => t("cond.removeValue", { v }) }} />}
                  </RhfField>
                  <RhfField control={form.control} name="risk" label={t("landers.f.risk")} plain>
                    {(field) => (
                      <SegmentedControl<string>
                        aria-label={t("landers.f.risk")}
                        value={String(field.value)}
                        onValueChange={(v) => field.onChange(Number(v))}
                        options={RISKS.map((r) => ({ value: String(r), label: t(riskLabelKey(r)) }))}
                      />
                    )}
                  </RhfField>
                  <Alert>
                    <Check className="size-4" />
                    <AlertDescription>{t("landers.create.after")}</AlertDescription>
                  </Alert>
                </>
              )}
            </div>
            <SheetFooter className="shrink-0 flex-row items-center gap-2 border-t border-border px-5 py-3 sm:justify-between">
              <span className="text-xs text-muted-foreground">
                {current === "studio" ? t("landers.create.studio.foot") : busy ? t("landers.create.working") : current === "zip" ? t("landers.create.zipFoot") : t("landers.create.linkFoot")}
              </span>
              <span className="flex gap-2">
                <Button type="button" variant="secondary" onClick={close} disabled={busy}>
                  {t("landers.create.cancel")}
                </Button>
                {current === "studio" ? null : (
                  <Button type="submit" disabled={busy}>
                    {busy ? t("landers.create.working") : t("landers.create.submit")}
                  </Button>
                )}
              </span>
            </SheetFooter>
          </form>
        </Form>
      </SheetContent>
    </Sheet>
  );
}
